import type { SharedSignInMethod, SignInPage } from '@server/shared-sign-in';
import type { SignupAnswers, SignupRegion } from '@server/signup-answers';
import { z } from 'zod';

import { getSessionHandoff, type LoginRequest, type MfaChallenge, rudderstackLogin } from './rudderstack-auth-client';
import { signInErrorMessage } from './sign-in-errors';
import { WEB_APP_ENTRY } from './web-app-entry';

const SessionCreatedSchema = z.object({ ok: z.literal(true), redirectTo: z.string().min(1) });
const SessionRefusedSchema = z.object({ ok: z.literal(false), error: z.string().min(1) });
const SessionCheckedSchema = z.object({
  ok: z.literal(true),
  redirectTo: z.string().min(1).nullable(),
  outcome: z.enum(['current', 'renewed', 'switched', 'signed_in']),
  userId: z.string().min(1),
});

/**
 * What the server did with a code from the shared session, and the user of Lookout's session now. See
 * `finishSharedSignIn` on the server.
 */
export type SessionCheck = Pick<z.infer<typeof SessionCheckedSchema>, 'outcome' | 'userId'>;

export type { SignupAnswers };

export interface SessionTokens {
  idToken: string;
  refreshToken: string;
}

/** Lookout refused to start its session. `code` is the refusal code, or `login_failed` for a network error. */
export class LookoutSessionError extends Error {
  readonly code: string;
  constructor(message: string, code: string) {
    super(message);
    this.code = code;
    this.name = 'LookoutSessionError';
  }
}

/**
 * config-backend accepted the sign-in, but Lookout did not start its session. The password and any MFA code are
 * used, and a backup code works only once, so a retry sends these tokens again instead. With the shared session,
 * `tokens` is null: the session stays at config-backend, and a retry reads it again.
 */
export class SessionNotStartedError extends Error {
  readonly tokens: SessionTokens | null;
  constructor(message: string, tokens: SessionTokens | null) {
    super(message);
    this.tokens = tokens;
    this.name = 'SessionNotStartedError';
  }
}

/**
 * What `/auth/session` starts from: a one-time code from the shared session (ACT2-879), or, without the shared session,
 * the ID token and the refresh token from config-backend's `/login`.
 */
type SessionCredentials = { code: string } | SessionTokens;

/**
 * `POST /auth/session`: Lookout checks the credentials, sets its own session cookie and names the page to open. With
 * the shared session, `method` tells analytics how the page got the session.
 */
export async function startLookoutSession(
  input: SessionCredentials & {
    returnTo: string | null;
    signup?: SignupAnswers;
    method?: SharedSignInMethod;
    entry?: typeof WEB_APP_ENTRY;
  },
): Promise<string> {
  const credentials =
    'code' in input ? { code: input.code } : { idToken: input.idToken, refreshToken: input.refreshToken };
  let res: Response;
  try {
    res = await fetch('/auth/session', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...credentials,
        ...(input.returnTo ? { returnTo: input.returnTo } : {}),
        ...(input.signup ? { signup: input.signup } : {}),
        ...(input.method ? { method: input.method } : {}),
        ...(input.entry ? { entry: input.entry } : {}),
      }),
    });
  } catch {
    throw new LookoutSessionError(signInErrorMessage('login_failed'), 'login_failed');
  }
  const body: unknown = await res.json().catch(() => null);
  const created = SessionCreatedSchema.safeParse(body);
  if (res.ok && created.success) return created.data.redirectTo;
  const refused = SessionRefusedSchema.safeParse(body);
  const code = refused.success ? refused.data.error : 'login_failed';
  throw new LookoutSessionError(signInErrorMessage(code), code);
}

/**
 * `POST /auth/session` for a page that already has a session (ACT2-879). With `renew`, the server always continues
 * the session on a newer ID token, as a re-auth needs. Without it, the server only compares the code's user with the
 * session's user, and renews in the last minutes of the session.
 */
export async function checkLookoutSession(code: string, options: { renew?: boolean } = {}): Promise<SessionCheck> {
  let res: Response;
  try {
    res = await fetch('/auth/session', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, method: 'shared_session', ...(options.renew ? {} : { check: true }) }),
    });
  } catch {
    throw new LookoutSessionError(signInErrorMessage('login_failed'), 'login_failed');
  }
  const body: unknown = await res.json().catch(() => null);
  const checked = SessionCheckedSchema.safeParse(body);
  if (res.ok && checked.success) return { outcome: checked.data.outcome, userId: checked.data.userId };
  const refused = SessionRefusedSchema.safeParse(body);
  const error = refused.success ? refused.data.error : 'login_failed';
  throw new LookoutSessionError(signInErrorMessage(error), error);
}

export type SignInOutcome =
  | { kind: 'signed_in'; redirectTo: string }
  | { kind: 'mfa_challenge'; challenge: MfaChallenge }
  | { kind: 'mfa_setup' };

/**
 * Starts Lookout's session from config-backend's shared session (ACT2-879): a one-time code from `/session/handoff`,
 * then `/auth/session`. `no_session` means that config-backend has no session for this browser.
 */
export async function startFromSharedSession(
  apiBaseUrl: string,
  input: { returnTo: string | null; method: SharedSignInMethod; signup?: SignupAnswers; fromWebApp?: boolean },
): Promise<{ kind: 'signed_in'; redirectTo: string } | { kind: 'no_session' }> {
  const handoff = await getSessionHandoff(apiBaseUrl);
  if (handoff.kind === 'ended') return { kind: 'no_session' };
  if (handoff.kind === 'unavailable') throw new LookoutSessionError(signInErrorMessage('login_failed'), 'login_failed');
  // The server records the sign-in start of a silent start, and only when a new session starts. So it needs to know
  // about a visit from the web app's sidebar.
  const redirectTo = await startLookoutSession({
    code: handoff.code,
    returnTo: input.returnTo,
    method: input.method,
    ...(input.signup ? { signup: input.signup } : {}),
    ...(input.fromWebApp && input.method === 'shared_session' ? { entry: WEB_APP_ENTRY } : {}),
  });
  return { kind: 'signed_in', redirectTo };
}

/**
 * One `/login` call at config-backend; tokens go straight on to Lookout's session. `pending` names an earlier `/login`
 * whose Lookout session did not start: its tokens, or null when the shared session keeps them. Then it skips `/login`.
 */
export async function signInToLookout(
  config: { apiBaseUrl: string; sharedSession: boolean },
  request: LoginRequest,
  options: { returnTo: string | null; signup?: SignupAnswers; pending?: SessionTokens | null },
): Promise<SignInOutcome> {
  const { returnTo, signup, pending } = options;
  if (config.sharedSession) return signInWithSharedSession(config.apiBaseUrl, request, options);

  let tokens = pending ?? undefined;
  if (!tokens) {
    const result = await rudderstackLogin(config.apiBaseUrl, request);
    if (result.kind !== 'tokens') return result;
    // Without the shared session, config-backend returns the refresh token, and Lookout's session needs it.
    if (!result.refreshToken) throw new LookoutSessionError(signInErrorMessage('login_failed'), 'login_failed');
    tokens = { idToken: result.idToken, refreshToken: result.refreshToken };
  }
  try {
    const redirectTo = await startLookoutSession({ ...tokens, returnTo, signup });
    return { kind: 'signed_in', redirectTo };
  } catch (error) {
    // Only a server or network failure can pass on a retry. A refusal (`invalid_token`, `invalid_request`,
    // `sign_in_unavailable`) fails the same way again, so the next attempt signs in again.
    if (error instanceof LookoutSessionError && error.code === 'login_failed') {
      throw new SessionNotStartedError(error.message, tokens);
    }
    throw error;
  }
}

/** The password sign-in with the shared session: `/login` starts it, and Lookout's session starts from it. */
async function signInWithSharedSession(
  apiBaseUrl: string,
  request: LoginRequest,
  options: { returnTo: string | null; signup?: SignupAnswers; pending?: SessionTokens | null },
): Promise<SignInOutcome> {
  if (options.pending === undefined) {
    const result = await rudderstackLogin(apiBaseUrl, request, { sharedSession: true });
    if (result.kind !== 'tokens') return result;
  }
  let started;
  try {
    started = await startFromSharedSession(apiBaseUrl, {
      returnTo: options.returnTo,
      method: 'password',
      signup: options.signup,
    });
  } catch (error) {
    // A retry reads the shared session again, with no second `/login`.
    if (error instanceof LookoutSessionError && error.code === 'login_failed') {
      throw new SessionNotStartedError(error.message, null);
    }
    throw error;
  }
  // `/login` accepted the password, but the browser kept no session cookie. The next attempt signs in again.
  if (started.kind === 'no_session') throw new LookoutSessionError(signInErrorMessage('login_failed'), 'login_failed');
  return { kind: 'signed_in', redirectTo: started.redirectTo };
}

/**
 * Lookout's start of Cognito's hosted sign-in for Google or an SSO identity provider. `fromWebApp`
 * marks a visit from the web app's sidebar, so the start event counts it. `region` makes a Google start a sign-up:
 * without it, Lookout creates no new account with a RudderStack organization. `page` is where an error returns.
 */
export function cognitoStartUrl(
  target: { provider: 'google' } | { provider: 'sso'; idp: string },
  options: { returnTo: string | null; invite: boolean; fromWebApp?: boolean; region?: SignupRegion; page?: SignInPage },
): string {
  const params = new URLSearchParams({ provider: target.provider });
  if (target.provider === 'sso') params.set('idp', target.idp);
  if (options.returnTo) params.set('return_to', options.returnTo);
  if (options.invite) params.set('invite', '1');
  if (options.region) params.set('region', options.region);
  if (options.fromWebApp) params.set('entry', WEB_APP_ENTRY);
  if (options.page && options.page !== 'login') params.set('page', options.page);
  return `/auth/cognito/start?${params.toString()}`;
}

const FederatedCheckedSchema = z.object({ ok: z.literal(true), method: z.enum(['google', 'sso']) });
const FederatedRefusedSchema = z.object({
  ok: z.literal(false),
  error: z.string().min(1),
  redirectTo: z.string().min(1),
});

/**
 * `POST /auth/federated`: Lookout's checks for a Google or SSO sign-in with the shared session (ACT2-879), then
 * config-backend's /authSignin. A refusal names the page to open next: `page` with the error, or the invite page.
 */
export async function checkFederatedSignIn(input: {
  idToken: string;
  invite: boolean;
  region?: SignupRegion;
  returnTo: string | null;
  page: SignInPage;
}): Promise<{ kind: 'checked'; method: 'google' | 'sso' } | { kind: 'refused'; redirectTo: string }> {
  let res: Response;
  try {
    res = await fetch('/auth/federated', {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        idToken: input.idToken,
        invite: input.invite,
        ...(input.region ? { region: input.region } : {}),
        ...(input.returnTo ? { returnTo: input.returnTo } : {}),
        page: input.page,
      }),
    });
  } catch {
    throw new LookoutSessionError(signInErrorMessage('login_failed'), 'login_failed');
  }
  const body: unknown = await res.json().catch(() => null);
  const checked = FederatedCheckedSchema.safeParse(body);
  if (res.ok && checked.success) return { kind: 'checked', method: checked.data.method };
  const refused = FederatedRefusedSchema.safeParse(body);
  if (refused.success) return { kind: 'refused', redirectTo: refused.data.redirectTo };
  throw new LookoutSessionError(signInErrorMessage('login_failed'), 'login_failed');
}
